import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../../utils/config';
import { useTranslation } from 'react-i18next';
import { parseMultiLangContent } from '../../utils/imageHelper';
import { getActiveLanguage } from '../../i18n';
import { COLORS, FONTS } from '../../theme/tokens';
import { FORM_FIELD_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from '../../theme/v2Styles';

const QuickAddModal = ({ open, onClose, product, onAddToCart }) => {
  const { t, i18n } = useTranslation();
  const activeLang = getActiveLanguage();
  void i18n.language;
  const [selectedVariation, setSelectedVariation] = useState('');
  const [comment, setComment] = useState('');
  const [commentCustom, setCommentCustom] = useState('');
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);

  // Fetch variations if product has them
  useEffect(() => {
    if (open && product && product.variation_id_prod && product.variation_id_prod > 0) {
      fetchVariations();
    } else {
      setVariations([]);
    }
  }, [open, product]);

  const fetchVariations = async () => {
    try {
      setLoading(true);
      // Fetch children of the parent variation using public endpoint
      const response = await fetch(`${API_URL}/variations/${product.variation_id_prod}/public`);
      if (response.ok) {
        const responseData = await response.json();
        const childVariations = responseData.data?.children || [];
        setVariations(childVariations);
        // Auto-select first variation if only one exists
        if (childVariations.length === 1) {
          setSelectedVariation(childVariations[0].variation_id.toString());
        }
      } else {
        console.error('Failed to fetch variations:', response.status);
      }
    } catch (error) {
      console.error('Error fetching variations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Validate that variation is selected if there are variations
    if (variations.length > 0 && !selectedVariation) {
      alert('Please select a variation');
      return;
    }

    // Find the selected variation name
    const selectedVariationObj = variations.find(v => v.variation_id.toString() === selectedVariation);
    const selectedVariationName = selectedVariationObj
      ? parseMultiLangContent(selectedVariationObj.variation_title, activeLang)
      : null;

    const cartItem = {
      product_id: product.product_id,
      quantity: quantity,
      selectedVariationName: selectedVariationName,
      productComment: comment || null,
      productCommentCustom: commentCustom || null
    };
    onAddToCart(cartItem);
    handleClose();
  };

  const handleClose = () => {
    setSelectedVariation('');
    setComment('');
    setCommentCustom('');
    setQuantity(1);
    onClose();
  };

  if (!product) return null;

  const productTitle = parseMultiLangContent(product.product_title, activeLang);
  const hasVariations = variations.length > 0;
  // Can only add to cart if: no variations OR variation is selected
  const canAddToCart = !hasVariations || (hasVariations && selectedVariation);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 640, borderRadius: '2px', boxShadow: '0 18px 50px rgba(35,48,42,0.18)' } }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontFamily: FONTS.heading, fontWeight: 600 }}>Quick Add to Cart</Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Close quick add">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Product Title */}
          <Typography variant="subtitle1" fontWeight="bold">
            {productTitle}
          </Typography>

          {/* Product Price */}
          <Typography variant="h6" sx={{ color: COLORS.green, fontWeight: 700 }}>
            €{typeof product.product_price === 'number' ? product.product_price.toFixed(2) : '0.00'}
          </Typography>

          {/* Quantity */}
          <FormControl fullWidth>
            <TextField
              label={t('product.quantity')}
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: product.product_quantity || 999 }}
              sx={FORM_FIELD_SX}
            />
          </FormControl>

          {/* Variations Dropdown */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : hasVariations ? (
            <FormControl fullWidth required>
              <InputLabel>Select Variation *</InputLabel>
              <Select
                value={selectedVariation}
                onChange={(e) => setSelectedVariation(e.target.value)}
                label={t('quickAdd.selectVariationRequired')}
                required
                sx={{ borderRadius: '2px', minHeight: 50 }}
              >
                <MenuItem value="" disabled>
                  <em>Please select...</em>
                </MenuItem>
                {variations.map((variation) => (
                  <MenuItem key={variation.variation_id} value={variation.variation_id.toString()}>
                    {parseMultiLangContent(variation.variation_title, activeLang)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {/* Standard Comment Field (if enabled) */}
          {product.enable_comment && (
            <TextField
              label={t('product.commentOptional')}
              multiline
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('quickAdd.commentPlaceholder')}
              fullWidth
              sx={FORM_FIELD_SX}
            />
          )}

          {/* Custom Comment Field (if enabled) */}
          {product.enable_comment_custom && (
            <TextField
              label={product.custom_comment_title || t('product.customCommentOptional')}
              multiline
              rows={2}
              value={commentCustom}
              onChange={(e) => setCommentCustom(e.target.value)}
              placeholder={t('quickAdd.customCommentPlaceholder')}
              fullWidth
              sx={FORM_FIELD_SX}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 3, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={SECONDARY_BUTTON_SX}>
          {t('product.cancel')}
        </Button>
        <Button
          onClick={handleAddToCart}
          variant="contained"
          color="primary"
          disabled={!canAddToCart || product.product_quantity <= 0}
          sx={PRIMARY_BUTTON_SX}
        >
          {t('product.addToCart')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddModal;
